/**
 * LighterSkillEffect — Fire tree foundation active: point-blank burn DoT.
 *
 * The player's father's Zippo. Holds a flame against a target within melee
 * range, applying a `burning` DoT at 3 damage/second for 3 seconds. Not a
 * projectile — the hit requires the target to be within one tile of the player.
 *
 * Status effect application is declarative via skill-effect-declarations.json.
 * The SkillEffectDeclarationEngine resolves upgrade bonuses at dispatch time:
 * - `dotDamagePerSecond` (additive, scoped to 'lighter') — +1/s from Tinder
 * - `dotDurationMs`      (additive, scoped to 'lighter') — +1000ms from Catchwork
 *
 * Fizzle behavior: on failed rollSkillSuccess the flame doesn't catch, but
 * the cooldown is still consumed (the lighter still fired). Out-of-range
 * attempts do not consume cooldown.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see fire-spec.md § Lighter
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface LighterDeps {
  /**
   * Dispatch declarative status effects via SkillEffectDeclarationEngine.
   * The engine reads skill-effect-declarations.json and resolves
   * params/conditions — the skill no longer manages effect application.
   */
  readonly dispatchEffects: (trigger: string, targetId?: string) => void;
  /** Returns true if a valid target is within melee range (≤ 1 tile). */
  readonly isInMeleeRange: () => boolean;
  /** Returns true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface LighterConfig {
  /** Cooldown in ms after activation or fizzle. Default: 400 (fast tier). */
  readonly cooldownMs: number;
}

const DEFAULT_CONFIG: LighterConfig = {
  cooldownMs: 400,            // fast cooldown tier
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class LighterSkillEffect implements SkillEffect {
  readonly skillId = 'lighter';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: LighterDeps;
  private readonly config: LighterConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _outOfRangeAttempts = 0;

  constructor(deps: LighterDeps, config: Partial<LighterConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get outOfRangeAttempts(): number { return this._outOfRangeAttempts; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Range check — out-of-range does not consume cooldown
    if (!this.deps.isInMeleeRange()) {
      this._outOfRangeAttempts++;
      return false;
    }

    // Fizzle check — cooldown IS consumed even on fizzle (the lighter fired)
    if (!this.deps.rollSkillSuccess('lighter')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Dispatch burning DoT via declaration engine
    this.deps.dispatchEffects('activation');

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
